import { Component, ViewChild, Inject, OnInit, OnDestroy, Renderer2 } from '@angular/core';
import { InquiryService } from '../../../services/inquiry.service';
import { Inquiry } from '../../../models/inquiry.model';
import { CommonModule, DOCUMENT } from '@angular/common';
import { MaterialModule } from '../../../material.module';
import { Router, RouterModule } from '@angular/router';
import { ColDef, ColGroupDef, FilterChangedEvent, GetRowIdParams, GridApi, GridOptions, GridReadyEvent, GridState, PaginationChangedEvent, ValueFormatterParams } from 'ag-grid-community';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import { compareAccountNo, decimalCellRenderer, escapeHTML, floor2dpString, formatDate, formatDecimalTrim, getAdToYm, getNextAd, getPool, getPrevAd, nowTimestamp, numericComparator, percentToRate, rateToPercent, toNum } from '../../../misc/functions';
import { FormsModule } from '@angular/forms';
import { ModalSnackbarService } from '../../../services/modal-snackbar.service';
import { LabelChipRendererComponent } from '../../label-chip/label-chip-renderer.componemt';
import { AG_GRID_LOCALE_JP } from '../../../misc/ag-grid-locale';
import { CustomSelectFilterComponent, CustomTextFilterComponent } from '../../../misc/custom-filter.component';
import { AdminUser, AdminUsersService } from '../../../services/admin-users.service';
import { MyGridState } from '../../../models/grid-state.model';
import { PoolTypeMap, PoolTypes, WeeklyReport, WeeklyReportResponse } from '../../../models/weekly-report.model';
import { WeeklyReportService } from '../../../services/weekly-report.service';
import { firstValueFrom } from 'rxjs';
import { MonthlyReport, MonthlyReportIndex, MonthlyReportIndexStatus } from '../../../models/monthly-report.model';
import { MonthlyReportService } from '../../../services/monthly-report.service';
import { MatStepper } from '@angular/material/stepper';
import { MatDialog } from '@angular/material/dialog';
import { AdminMonthlyReportEditDialogComponent } from './admin-monthly-report-edit.dialog.component/admin-monthly-report-edit.dialog.component';
import { AdminMonthlyReportCreateDialogComponent } from './admin-monthly-report-create.dialog.component/admin-monthly-report-create.dialog.component';

@Component({
  selector: 'app-admin-monthly-report',
  standalone: true,
  imports: [CommonModule, MaterialModule, RouterModule, AgGridModule, FormsModule, LabelChipRendererComponent],
  templateUrl: './admin-monthly-report.component.html',
  styleUrls: ['./admin-monthly-report.component.scss']
})
export class AdminMonthlyReportComponent implements OnInit, OnDestroy {
  private mainEl!: HTMLElement;

  isLoaded = false;
  localeText = AG_GRID_LOCALE_JP;

  vfTrim = (p: ValueFormatterParams) => formatDecimalTrim(p.value, 'ja-JP');
  toNum = toNum;
  numericComparator = numericComparator;
  getPool = getPool;
  rateToPercent = rateToPercent;
  compareAccountNo = compareAccountNo;

  defaultColDef: ColDef = {
    filter: 'agTextColumnFilter',
    autoHeaderHeight: true,
    wrapHeaderText: true,
    floatingFilter: true,
    suppressHeaderMenuButton: true,
    suppressFloatingFilterButton: true,
    suppressFiltersToolPanel: true,
    floatingFilterComponent: CustomTextFilterComponent,
    editable: false,
    sortable: true
  };

  columnDefs: ColDef[] = [
    { field: 'login_user_id', headerName: 'login', width: 105, pinned: 'left' },
    { field: 'user_name', headerName: '氏名', width: 115, pinned: 'left' },
    { field: 'pool', headerName: 'POOL', width: 95, pinned: 'left', valueFormatter: p => this.getPool(p.data.cap_id), filterValueGetter: p => this.getPool(p.data.cap_id)},
    { field: 'cap_id', headerName: '口座番号', width: 105, pinned: 'left', comparator: this.compareAccountNo },

    { field: 'cur', headerName: '通貨', width: 65, pinned: 'left' },
    { field: 'rate', headerName: 'レート', width: 80, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      filterValueGetter: p => this.toNum(p.data.rate)},

    { field: 'hdc', headerName: '成功報酬料率(%)', width: 70, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      valueGetter: p => this.rateToPercent(this.toNum(p.data.hdc)?? 0)},

    { field: 'monthly_management_fee', headerName: '管理手数料率(%)', width: 95, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      valueGetter: p => this.rateToPercent(this.toNum(p.data.monthly_management_fee) ?? 0)},

    { field: 'before_high_water_mark', headerName: '前月ハイウォーターマーク', width: 125, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      filterValueGetter: p => this.toNum(p.data.before_high_water_mark)  },

    { field: 'bal_a', headerName: '前月末残高', width: 125, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      filterValueGetter: p => this.toNum(p.data.bal_a) },

    { field: 'nyukin', headerName: '入金額', width: 105, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      filterValueGetter: p => this.toNum(p.data.nyukin)  },

    { field: 'adjust_amount', headerName: '調整額', width: 105, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      filterValueGetter: p => this.toNum(p.data.adjust_amount)  },
    { field: 'adjust_comment', headerName: '調整コメント', width: 100 },

    { field: 'nyukin_high_water_mark', headerName: '入金・調整後ハイウォーターマーク', width: 125, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      filterValueGetter: p => this.toNum(p.data.nyukin_high_water_mark)  },

    { field: 'balance_start', headerName: '月初残高', width: 125, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      filterValueGetter: p => this.toNum(p.data.balance_start)  },

    { field: 'balance_start_jpy', headerName: '月初残高(JPY)', width: 125, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      filterValueGetter: p => this.toNum(p.data.balance_start_jpy)  },

    { field: 'swap_per', headerName: 'スワップ損益(%)', width: 95, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      valueGetter: p => this.rateToPercent(this.toNum(p.data.swap_per) ?? 0)},

    { field: 'trade_per', headerName: '取引損益(%)', width: 95, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      valueGetter: p => this.rateToPercent(this.toNum(p.data.trade_per) ?? 0)},

    { field: 'monthly_per', headerName: '月間損益(%)', width: 95, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      valueGetter: p => this.rateToPercent(this.toNum(p.data.monthly_per) ?? 0)},

    { field: 'swap_pl', headerName: 'スワップ損益', width: 95, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      filterValueGetter: p => this.toNum(p.data.swap_pl)  },

    { field: 'trade_pl', headerName: '取引損益', width: 95, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      filterValueGetter: p => this.toNum(p.data.trade_pl)  },

    { field: 'monthly_pl', headerName: '月間損益', width: 95, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      filterValueGetter: p => this.toNum(p.data.monthly_pl)  },

    { field: 'balance_before_fees', headerName: '手数料徴収前残高', width: 125, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      filterValueGetter: p => this.toNum(p.data.balance_before_fees)  },

    { field: 'high_water_mark', headerName: '当月ハイウォーターマーク', width: 125, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      filterValueGetter: p => this.toNum(p.data.high_water_mark)  },

    { field: 'success_fee_pl', headerName: '成功報酬対象損益', width: 120, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      filterValueGetter: p => this.toNum(p.data.success_fee_pl)  },

    { field: 'hc_a', headerName: '成功報酬手数料', width: 120, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      filterValueGetter: p => this.toNum(p.data.hc_a)  },

    { field: 'management_fee', headerName: '月間管理手数料', width: 120, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      filterValueGetter: p => this.toNum(p.data.management_fee)  },

    { field: 'hc_b', headerName: 'その他手数料', width: 120, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      filterValueGetter: p => this.toNum(p.data.hc_b)  },
    { field: 'hc_b_comment', headerName: 'その他手数料コメント', width: 100 },

    { field: 'balance_after_fees', headerName: '手数料徴収後残高', width: 125, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      filterValueGetter: p => this.toNum(p.data.balance_after_fees)  },

    { field: 'pension', headerName: '年金出金額', width: 100, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      filterValueGetter: p => this.toNum(p.data.pension)  },

    { field: 'shukkin', headerName: '出金額', width: 125, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      filterValueGetter: p => this.toNum(p.data.shukkin)  },
    { field: 'is_full_withdrawal', headerName: '全額出金', width: 46, valueGetter: p => p.data.is_full_withdrawal ? '✔' : null, filterValueGetter: p => p.data.is_full_withdrawal ? '✔' : null},

    { field: 'transfer_amount', headerName: '振替額', width: 125, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      filterValueGetter: p => this.toNum(p.data.transfer_amount)  },
    { field: 'is_full_transfer', headerName: '全額振替', width: 46, valueGetter: p => p.data.is_full_transfer ? '✔' : null, filterValueGetter: p => p.data.is_full_transfer ? '✔' : null},

    { field: 'transfer_pool', headerName: '振替先POOL', width: 65, sortable: true},

    { field: 'transfer_hdc', headerName: '振替後の成功報酬料率(%)', width: 70, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      valueGetter: p => this.rateToPercent(this.toNum(p.data.transfer_hdc)?? 0)},

    { field: 'transfer_note', headerName: '振替後の備考', width: 100 },

    { field: 'bal_b', headerName: '月末残高', width: 125, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      filterValueGetter: p => this.toNum(p.data.bal_b)  },

    { field: 'bal_b_jpy', headerName: '月末残高(JPY)', width: 125, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      filterValueGetter: p => this.toNum(p.data.bal_b_jpy)  },

    { field: 'eom_high_water_mark', headerName: '当月末ハイウォーターマーク', width: 125, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      filterValueGetter: p => this.toNum(p.data.eom_high_water_mark)  },

    { field: 'rate_b', headerName: '手数料徴収後収益率(%)', width: 95, cellRenderer: decimalCellRenderer, valueFormatter: this.vfTrim, cellClass: 'decimal-cell', comparator: this.numericComparator, filter: 'agNumberColumnFilter',
      valueGetter: p => this.rateToPercent(this.toNum(p.data.rate_b) ?? 0)},

    { field: 'status', headerName: '状態', width: 100 },
    { field: 'transfer_from', headerName: '振替元口座', width: 105 },
    { field: 'flg', headerName: 'JACは共同名義', width: 100 },
    { field: 'cmm', headerName: '共同名義者名', width: 100 },
    { field: 'enroll', headerName: '運用開始日', width: 100 },
    { field: 'res_b', headerName: '拘束期間', width: 65 },
    { field: 'chg', headerName: '拘束期間終了日', width: 100 },
    { field: 'cancel', headerName: '解約日', width: 100 },
    { field: 'note', headerName: '備考', width: 100 },
  ]

  currentAd: number = 0;
  prevAd: number = 0;
  nextAd: number = 0;

  poolTypes = PoolTypeMap;

  pl?: WeeklyReportResponse;
  last?: MonthlyReportIndex;

  index?: MonthlyReportIndex;
  data: MonthlyReport[] = [];

  gridApi?: GridApi<MonthlyReport>;

  @ViewChild('stepper', { static: true }) stepper!: MatStepper;

  constructor(
    private router: Router,
    private weeklyService: WeeklyReportService,
    private monthlyService: MonthlyReportService,
    private snackBar: ModalSnackbarService,
    private dialog: MatDialog,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document
  ) {}

  export() {
    if (this.gridApi) {
      const filename = this.currentAd + 'の月報_' + nowTimestamp() + '.csv';

      this.gridApi.exportDataAsCsv({
        fileName: filename
      });
    }
  }

  ngOnInit(): void {
    this.isLoaded = true;
    this.goToStep(0); // ページロード時は初期状態にしておく
    this.initialLoad();

    this.mainEl = this.document.getElementById('main')!;
    if (this.mainEl) {
      this.renderer.addClass(this.mainEl, 'main-fullwidth');
    }
  }

  ngOnDestroy(): void {
    if (this.mainEl) {
      this.renderer.removeClass(this.mainEl, 'main-fullwidth');
    }
  }

  onGridReady(e: GridReadyEvent<MonthlyReport>) {
    this.gridApi = e.api;
  }

  setRowData(rows: MonthlyReport[]) {
    this.data = [...rows];
    this.gridApi?.setGridOption('rowData', this.data);
  }

  async initialLoad() {
    const ad = await firstValueFrom(this.weeklyService.getLatestAd());
    this.changeAd(ad);
  }

  getRowId = (params: GetRowIdParams<MonthlyReport>) => String(params.data.id);

  get ym() {
    return getAdToYm(this.currentAd);
  }

  changeStep(foreceStatus?: typeof MonthlyReportIndexStatus[number]) {
    // index が無ければ初期状態
    if (!this.index) {
      this.goToStep(0);
      return;
    }

    if (foreceStatus) {
      if (!confirm('処理状態を「' + foreceStatus + '」に変更します')) return;

      this.index.status = foreceStatus;
      // TODO: 強制的に状態を変えられるのを本番でも残すのであれば、サーバー側も変更するべき？
    }

    // index の状態で判定
    // '月初処理中', '月末処理中', '月報確定', '公開状態'
    this.goToStep(MonthlyReportIndexStatus.indexOf(this.index.status) + 1);
  }

  goToStep(index: number) {
    this.stepper.selectedIndex = index;
  }

  changeAd(ad: number) {
    this.pl = undefined;
    this.last = undefined;
    this.index = undefined;
    this.setRowData([]);

    this.currentAd = ad;
    this.prevAd = getPrevAd(ad);
    this.nextAd = getNextAd(ad);

    // 週報
    this.weeklyService.getAll(ad, true, false).subscribe({
      next: (res) => {
        if ((res as any).success === false) {
          this.pl = undefined;
        }
        else {
          this.pl = res;

          // 先月月報
          this.monthlyService.getAll(getPrevAd(ad), true, true).subscribe({
            next: (res) => {
              if (res) {
                this.last = res;
              }
            },
            error: () => this.snackBar.show(`先月の月報の読み込み失敗`)
          })

          // 当月月報
          this.monthlyService.getAll(ad, true).subscribe({
            next: (res) => {
              if (res && this.pl) {
                this.index = res;
                this.setRowData(res.reports);
              }
              this.changeStep();
            },
            error: () => this.snackBar.show(`当月の月報の読み込み失敗`)
          })
        }
      },
      error: () => this.snackBar.show('当月週報の読み込み失敗')
    });

  }

  get rateAvailable() {
    if (this.pl && this.pl[4] && this.pl[4].meta && !this.isMetaEmpty(this.pl[4].meta)) {
      return '為替レートあり';
    }
    return '為替レート登録なし';
  }

  isMetaEmpty(meta: Record<string, unknown> | null | undefined): boolean {
    if (!meta || Object.keys(meta).length === 0) {
      return true;
    }

    // 値の中に null が1つでもあれば true
    return Object.values(meta).some(x => x == null);  // == で null/undefined 両方引っ掛ける
  }

  // 月報データ初期作成ボタンが押せるか？
  get isCreatable() {
    return this.pl &&   // 当月の週報が存在する
      !this.isMetaEmpty(this.pl[4].meta) &&    // 当月の為替レートが入っている
      !this.index &&    // 当月の月報データはまだ無い
      this.last &&    // 先月の月報データはある
      this.last.reports_count > 0 && // 先月分の口座情報も入っている
      this.last.status === '公開状態'   // 先月の月報データが公開状態になっている
  }

  createMonthly() {
    if (!confirm('月報データ初期作成を行います')) return;

    this.monthlyService.stepToProcessing(this.currentAd, '処理開始前').subscribe({
      next: (res) => {
        if (res) {
          this.index = res;
          this.setRowData(res.reports);

          this.changeStep();
        }
      },
      error: () => this.snackBar.show(`当月の月報の生成失敗`)
    })
  }

  isPerEmpty(data: Record<string, WeeklyReport> | null | undefined): boolean {
    if (!data || Object.keys(data).length === 0) {
      return true;
    }

    return Object.values(data).some(item =>
//      (['trade_per', 'swap_per', 'sum_per']).some(
      (['sum_per']).some(   // TODO: 現状では sum_per しか入っていない
        key => (item as any)[key] == null  // == で null/undefined 両方引っ掛ける
      )
    );
  }

  // 月末処理に進むボタンが押せるか？
  get isReadyToEndProcessing() {
    return this.pl &&           // 当月の週報がある
      this.pl[4].is_public &&   // idx=4（月集計）のデータがある
      !this.isPerEmpty(this.pl[4].reports) // 月損益％が全部そろっている
  }

  stepToEndProcessing() {
    if (!confirm('月末処理に進みます')) return;

    if (!this.index) {
      this.snackBar.show(`当月の月報データがありません`);
      return;
    }

    // 月末処理の開始に必要なデータをサーバー側で生成する
    this.monthlyService.stepToProcessing(this.currentAd, this.index.status).subscribe({
      next: (res) => {
        if (res) {
          this.index = res;
          this.setRowData(res.reports);

          this.changeStep();
        }
      },
      error: () => this.snackBar.show(`月末処理の初期化に失敗`)
    });

  }

  finalize() {
    if (!confirm('月報を確定します')) return;

    if (!this.index) {
      this.snackBar.show(`当月の月報データがありません`);
      return;
    }

    this.monthlyService.stepToProcessing(this.currentAd, this.index.status).subscribe({
      next: (res) => {
        if (res) {
          this.index = res;
          this.setRowData(res.reports);

          this.changeStep();
        }
      },
      error: () => this.snackBar.show(`月末確定に失敗`)
    });
  }

  publish() {
    if (!confirm('月報を公開します')) return;

    if (!this.index) {
      this.snackBar.show(`当月の月報データがありません`);
      return;
    }

    this.monthlyService.stepToProcessing(this.currentAd, this.index.status).subscribe({
      next: (res) => {
        if (res) {
          this.index = res;
          this.setRowData(res.reports);

          this.changeStep();
        }
      },
      error: () => this.snackBar.show(`月末の公開処理に失敗`)
    });
  }

  unpublish() {
    if (!confirm('「月報確定（非公開）」に戻ります')) return;

    if (!this.index) {
      this.snackBar.show(`当月の月報データがありません`);
      return;
    }

    this.monthlyService.stepToProcessing(this.currentAd, this.index.status).subscribe({
      next: (res) => {
        if (res) {
          this.index = res;
          this.setRowData(res.reports);

          this.changeStep();
        }
      },
      error: () => this.snackBar.show(`月末の公開処理の引き戻しに失敗`)
    });
  }

  // TODO: 開発用
  revert() {
    if (!confirm('月報破棄(開発用) を行います')) return;

    if (!this.index) {
      this.snackBar.show(`当月の月報データがありません`);
      return;
    }

    this.monthlyService.stepToProcessing(this.currentAd, 'revert').subscribe({
      next: (res) => {
        this.index = undefined;
        this.setRowData([]);

        this.changeStep();
      },
      error: () => this.snackBar.show(`初期状態に戻る処理に失敗`)
    });
  }

  onRowClicked(event: any) {
    console.log(event.data);

    // 202507 より前はクリックできない
    if (this.currentAd < 202507) return;

    const dialogRef = this.dialog.open(AdminMonthlyReportEditDialogComponent, {
      width: '910px',
      maxWidth: '910px',
      position: {
        top: '48px',   // ← これを小さくするほど上に来る
      },
      disableClose: true,
      data: {report: event.data, status: this.index!.status},
    });

    dialogRef.afterClosed().subscribe(updated => {

      console.log('closed', updated);

      // updated が返って来なければ何もしない（キャンセルなど）
      if (!updated) return;

      // grid の該当行だけ更新
      const node = this.gridApi!.getRowNode(String(updated.id));
      if (node) {
        node.setData(updated); // ← 行データ差し替え
      } else {
         this.snackBar.show(`更新データの差し替えに失敗しました`)
      }
    });
  }

  newAccount(isRebuildMode = false) {

    const dialogRef = this.dialog.open(AdminMonthlyReportCreateDialogComponent, {
      width: '910px',
      maxWidth: '910px',
      maxHeight: 'calc(100vh - 96px)',
      position: {
        top: '48px',
      },
      panelClass: 'monthly-report-dialog',
      disableClose: true,
      data: {ad: this.currentAd, isRebuildMode: isRebuildMode}
    });

    dialogRef.afterClosed().subscribe(newRow => {

      console.log('closed', newRow);

      // updated が返って来なければ何もしない（キャンセルなど）
      if (!newRow) return;

      this.snackBar.show(`login: ${newRow['login_user_id']} に、口座番号：${newRow['cap_id']} が追加されました`, undefined, 5000);
      this.addRowToSameLoginUserLast(newRow);
    });

  }

  addRowToSameLoginUserLast(newRow: MonthlyReport) {

    if (!this.gridApi)  {
      // api が無ければリロード
      this.changeAd(this.currentAd);
    }
    else {
      const rows = [...this.data];

      let insertIndex = rows.length;
      for (let i = rows.length - 1; i >= 0; i--) {
        if (rows[i].login_user_id === newRow.login_user_id) {
          insertIndex = i + 1;
          break;
        }
      }

      rows.splice(insertIndex, 0, newRow);
      this.setRowData(rows);
    }
  }

}
